import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import SupportWidget from './SupportWidget';
import { useAssistant } from '../hooks/useAssistant';
import VoiceLayer from '../voice/VoicePanel';
import ReminderListener from './ReminderListener';
import api, { cachedGet } from '../api';
import { useT } from '../i18n';

function SubscriptionBanner() {
  const [banner, setBanner] = useState(null);
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    api.get('/settings/knowledge/tier')
      .then(res => {
        if (cancelled) return;
        const { daysRemaining, currentLabel, currentTier, subscriptionStatus } = res.data;
        if (currentTier === 0) return; // Free tier, no banner
        // Admin-granted or Stripe-active with no expiry: no banner
        if (daysRemaining == null) return; // null (permanent/free) or undefined (malformed payload) — never render raw template
        if (daysRemaining > 7) return; // > 7 days, don't show
        const msg = daysRemaining <= 0
          ? t('layout.sub_expired', { label: currentLabel })
          : t('layout.sub_expiring', { label: currentLabel, days: daysRemaining });
        setBanner({ msg, daysRemaining });
      })
      .catch(err => console.error('[SubscriptionBanner] fetch failed:', err.message));
    return () => { cancelled = true; };
  }, []);

  if (!banner) return null;

  const bg = banner.daysRemaining <= 0
    ? 'rgba(239,68,68,0.12)' : 'rgba(234,179,8,0.12)';
  const border = banner.daysRemaining <= 0
    ? 'rgb(239,68,68)' : 'rgb(234,179,8)';

  return (
    <div style={{
      padding: '10px 20px', fontSize: '14px', fontWeight: 500,
      backgroundColor: bg, borderBottom: `1px solid ${border}`,
      color: 'var(--text-primary)',
    }}>
      <span>⚠️ {banner.msg}</span>
    </div>
  );
}

function ApiUsageBanner() {
  const [worst, setWorst] = useState(null);
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      cachedGet('/external-chat/alert-status')
        .then(res => { if (!cancelled) setWorst(res.data.worst || null); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!worst) return null;
  const exhausted = worst.pct >= 100;
  const bg = exhausted ? 'rgba(239,68,68,0.12)' : 'rgba(234,179,8,0.12)';
  const border = exhausted ? 'rgb(239,68,68)' : 'rgb(234,179,8)';
  const msg = exhausted
    ? t('layout.api_quota_exhausted', { label: worst.label, used: worst.used_today, limit: worst.daily_limit })
    : t('layout.api_quota_warning', { label: worst.label, pct: worst.pct, used: worst.used_today, limit: worst.daily_limit });

  return (
    <div style={{
      padding: '10px 20px', fontSize: '14px', fontWeight: 500,
      backgroundColor: bg, borderBottom: `1px solid ${border}`,
      color: 'var(--text-primary)',
    }}>
      <span>⚠️ {msg}</span>
    </div>
  );
}

function LeadCaptureBanner() {
  const [unread, setUnread] = useState(0);
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      cachedGet('/external-chat/alert-status')
        .then(res => { if (!cancelled) setUnread(res.data.unread_leads || 0); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!unread) return null;
  return (
    <div style={{
      padding: '10px 20px', fontSize: '14px', fontWeight: 500,
      backgroundColor: 'rgba(34,197,94,0.12)', borderBottom: '1px solid rgb(34,197,94)',
      color: 'var(--text-primary)',
    }}>
      <span>🎉 {t('layout.new_leads', { count: unread })} </span>
      <Link to="/settings?oauth_tab=interaction" style={{ color: 'var(--text-brand)', textDecoration: 'underline' }}>
        {t('layout.view_leads')}
      </Link>
    </div>
  );
}

// W28 试用横幅: 全程常驻细条 (天数+余额+选方案入口), 到期/credit 耗尽升级样式
function TrialBanner() {
  const [wallet, setWallet] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardMsg, setCardMsg] = useState(null);
  const [trialCfg, setTrialCfg] = useState(null);
  const t = useT();
  const location = useLocation();

  // 绑卡回跳: card_ok → 提示补赠额 (来自 admin 配置); card_cancel → 静默清参
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const trial = q.get('trial');
    if (trial === 'card_ok' && !trialCfg) return; // 配置未加载时保留参数, 加载后重跑
    if (trial === 'card_ok') {
      setCardMsg({ ok: true, text: t('layout.trial_card_ok', { credits: Number(trialCfg.card_bonus_credits || 0).toLocaleString('en-US') }) });
    } else if (trial === 'card_cancel') {
      // 静默: 不提示, 仅清参
    }
    if (trial === 'card_ok' || trial === 'card_cancel') {
      const u = new URL(window.location.href);
      u.searchParams.delete('trial');
      window.history.replaceState({}, '', u.pathname + u.search);
    }
  }, [location.search, trialCfg, t]);

  const startCardTrial = async () => {
    if (cardBusy) return;
    setCardBusy(true);
    setCardMsg(null);
    try {
      const r = await api.post('/wallet/trial-card-checkout');
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else setCardMsg({ ok: false, text: r.data?.message || t('layout.trial_card_err') });
    } catch (e) {
      const d = e.response?.data;
      setCardMsg({ ok: false, text: d?.message || d?.error || t('layout.trial_card_err') });
    } finally {
      setCardBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      cachedGet('/wallet')
        .then(res => { if (!cancelled) setWallet(res.data); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    cachedGet('/wallet/trial-config')
      .then(res => { if (!cancelled) setTrialCfg(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!wallet?.is_trial) return null;
  const phase = wallet.trial_phase || 'active';
  const creditFrozen = wallet.locked_reason === 'trial_credit_exhausted';
  const daysLeft = wallet.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(wallet.trial_ends_at.replace(' ', 'T') + 'Z') - Date.now()) / 86400000))
    : null;

  const alertLevel = wallet.trial_alert_level;
  const styles = creditFrozen || phase === 'locked' || alertLevel === 'critical'
    ? { bg: 'rgba(239,68,68,0.12)', border: 'rgb(239,68,68)' }
    : alertLevel === 'low'
      ? { bg: 'rgba(234,179,8,0.12)', border: 'rgb(234,179,8)' }
      : { bg: 'var(--bg-card)', border: 'var(--border)' };

  const msg = creditFrozen
    ? t('layout.trial_credit_locked')
    : phase === 'locked'
      ? t('layout.trial_locked')
      : alertLevel === 'critical'
        ? t('layout.trial_balance_critical', { credits: Math.floor(wallet.balance_credits || 0).toLocaleString('en-US') })
        : alertLevel === 'low'
          ? t('layout.trial_balance_low', { credits: Math.floor(wallet.balance_credits || 0).toLocaleString('en-US') })
      : daysLeft != null
        ? t('layout.trial_active', { days: daysLeft, credits: Math.floor(wallet.balance_credits || 0) })
        : t('layout.trial_active_nodate', { credits: Math.floor(wallet.balance_credits || 0) });
  // 仅试用期且未走绑卡轨时展示「绑卡升级试用」入口
  const canCardTrial = wallet.plan?.trial_track !== 'card';
  const cardBonusCredits = Number(trialCfg?.card_bonus_credits || 0);

  return (
    <>
      <div style={{
        padding: '8px 20px', fontSize: '13px', fontWeight: 500,
        backgroundColor: styles.bg, borderBottom: `1px solid ${styles.border}`,
        color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span>{msg}</span>
        {phase === 'active' && canCardTrial && (
          <button onClick={startCardTrial} disabled={cardBusy}
            style={{ color: 'var(--text-brand)', textDecoration: 'underline', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: cardBusy ? 'wait' : 'pointer', fontSize: 13 }}>
            {cardBusy || !trialCfg ? '…' : t('layout.trial_card_cta', { credits: cardBonusCredits.toLocaleString('en-US') })}
          </button>
        )}
        <Link to="/usage#plans" style={{ color: 'var(--text-brand)', textDecoration: 'underline', fontWeight: 600 }}>
          {t('layout.choose_plan')}
        </Link>
      </div>
      {cardMsg && (
        <div style={{
          padding: '8px 20px', fontSize: '13px', fontWeight: 500,
          backgroundColor: cardMsg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
          borderBottom: `1px solid ${cardMsg.ok ? 'rgb(34,197,94)' : 'rgb(239,68,68)'}`,
          color: 'var(--text-primary)',
        }}>
          {cardMsg.ok ? '✅ ' : '⚠️ '}{cardMsg.text}
        </div>
      )}
    </>
  );
}

export default function Layout() {
  const { activeAssistant } = useAssistant();
  const location = useLocation();
  const t = useT();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // 路由切换时自动收起移动端抽屉
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  // 对话页输入区与右下角浮球重叠 — 对话页隐藏全局浮球, 改用页面内帮助入口
  const hideSupportWidget = /\/conversations(\/|$)/.test(location.pathname);
  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* 移动端遮罩 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <Sidebar onClose={() => setSidebarOpen(false)} open={sidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0">
        {/* 移动端顶部栏: 汉堡菜单 */}
        <div
          className="flex items-center justify-between md:hidden px-3 h-12 border-b shrink-0"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-sidebar)' }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 rounded-lg"
            style={{ color: 'var(--text-secondary)' }}
            aria-label={t('common.open_menu')}
          >
            <Menu size={20} />
          </button>
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>AI</span>
        </div>
        <TrialBanner />
        <SubscriptionBanner />
        <ApiUsageBanner />
        <LeadCaptureBanner />
        <main className="flex-1 overflow-auto min-w-0">
          <Outlet />
        </main>
      </div>
      {!hideSupportWidget && <SupportWidget />}
      {/* 语音层：沉浸/悬浮/关框三态 + 告知/达量/冲突弹窗（S2） */}
      <VoiceLayer />
      {/* V-CARE-05 全局提醒监听：到期事件实时 toast / 桌面通知 */}
      <ReminderListener />
    </div>
  );
}
